import React, { useEffect, useRef, useState } from 'react';
import styles from './StartTimeModal.module.scss';
import { ReactComponent as AlarmIcon } from "assets/blackAlarm.svg";

interface StartTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  startTime: string;
  setStartTime: (startTime: string) => void;
  setIsStartTimeModalOpen: (isOpen: boolean) => void;
}

const StartTimeModal = ({isOpen, setIsStartTimeModalOpen, startTime, setStartTime}: StartTimeModalProps) => {
  const now = new Date();
  const [selectedHours, setSelectedHours] = useState(String(now.getHours() % 12 || 12).padStart(2, '0'));
  const [selectedMinutes, setSelectedMinutes] = useState(String(now.getMinutes()).padStart(2, '0'));
  const [selectedPeriod, setSelectedPeriod] = useState(now.getHours() < 12 ? 'AM' : 'PM');

  const padList = (list: string[]) => ['', '', ...list, '', ''];
  const hours = padList(Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')));
  const minutes = padList(Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')));
  const periods = padList(['AM', 'PM']);

  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);
  const periodRef = useRef<HTMLDivElement>(null);

  const itemHeight = 40;

  const getCenteredValue = (scrollTop: number, list: string[]) => {
    const index = Math.round(scrollTop / itemHeight);
    return list[index + 2]; // because of padding
  };

  const handleScroll = (ref: React.RefObject<HTMLDivElement>, list: string[], setter: (value: string) => void) => {
    if (ref.current) {
      const top = ref.current.scrollTop;
      const newValue = getCenteredValue(top, list);
      if (newValue !== '') setter(newValue);
    }
  };

  const scrollToValue = (ref: React.RefObject<HTMLDivElement>, value: string, list: string[]) => {
    const index = list.indexOf(value);
    if (index !== -1 && ref.current) {
      ref.current.scrollTop = (index - 2) * itemHeight;
    }
  };

  useEffect(() => {
    scrollToValue(hoursRef, selectedHours, hours);
    scrollToValue(minutesRef, selectedMinutes, minutes);
    scrollToValue(periodRef, selectedPeriod, periods);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div style={{display: isOpen ? 'block' : 'none'}}>
      <div className={styles.overlay} onClick={() => setIsStartTimeModalOpen(false)} />
      <div className={styles.optsWrapper}>
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <div className={styles.headerWrapper}>
            <div style={{display: 'flex', alignItems: 'center', }}>
              <AlarmIcon style={{width: '20px', height: '20px'}} />
              <p className={styles.startTimeText}>Set Start Time</p>
            </div>
          </div>
          <div className={styles.inputWrapper}>
            <div className={styles.timeWheelsWrapper}>
              {/* Hours Wheel */}
              <div className={`${styles.wheelContainer} ${styles.rightBorderZeroRadius}`}>
                <div
                  ref={hoursRef}
                  className={styles.customWheel}
                  onScroll={() => handleScroll(hoursRef, hours, setSelectedHours)}
                >
                  {hours.map((h, i) => (
                    <div
                      key={i}
                      className={`${styles.wheelItem} ${h === selectedHours ? styles.selectedItem : ''}`}
                    >
                      {h}
                    </div>
                  ))}
                </div>
              </div>

              {/* Minutes Wheel */}
              <div className={`${styles.wheelContainer} ${styles.borderZeroRadius}`}>
                <div
                  ref={minutesRef}
                  className={`${styles.customWheel} `}
                  onScroll={() => handleScroll(minutesRef, minutes, setSelectedMinutes)}
                >
                  {minutes.map((m, i) => (
                    <div
                      key={i}
                      className={`${styles.wheelItem} ${m === selectedMinutes ? styles.selectedItem : ''}`}
                    >
                      {m}
                    </div>
                  ))}
                </div>
              </div>

              {/* Period Wheel */}
              <div className={`${styles.wheelContainer} ${styles.leftBorderZeroRadius}`}>
                <div
                  ref={periodRef}
                  className={styles.customWheel}
                  onScroll={() => handleScroll(periodRef, periods, setSelectedPeriod)}
                >
                  {periods.map((p, i) => (
                    <div
                      key={i}
                      className={`${styles.wheelItem} ${p === selectedPeriod ? styles.selectedItem : ''}`}
                    >
                      {p}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.btnWrapper}>
          <button 
            onClick={() => setIsStartTimeModalOpen(false)}
            style={{
              background: 'none',
              color: '#000',
              fontSize: '20px',
              borderRadius: '10px',
              border: 'none'
            }}
          >
            <span style={{color: '#E64B37'}}>CANCEL</span>
          </button>
        </div>
      </div>

      <div className={styles.doneWrapper}>
        <button 
          onClick={() => {
            setIsStartTimeModalOpen(false);
            const time = `${selectedHours}:${selectedMinutes} ${selectedPeriod}`;
            setStartTime(time);
          }}
          style={{
            background: 'none',
            color: '#4F7D9C',
            fontSize: '20px',
            margin: '5px',
            borderRadius: '10px',
            border: 'none'
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default StartTimeModal;